import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebaseConfig";
import LikeProfile from "./LikeProfile";
import { useUserData } from "../../hooks/useUsers";
import { Empty } from "antd";
import {
  Row,
  Col,
  Card,
  Button,
  QRCode,
  Typography,
  Image,
  Modal,
  Table,
  Space,
  Spin,
  Badge,
} from "antd";
import { 
  ArrowLeftOutlined, 
  QrcodeOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined,
  StarOutlined 
} from "@ant-design/icons";
import { getGunaPoints } from "../../lib/utils";

const { Title, Text, Paragraph } = Typography;
const { Meta } = Card;

function Profile() {
  const { id } = useParams();
  const [showMore, setShowMore] = useState(false);
  const [profile, setProfile] = useState();
  const [user] = useAuthState(auth);
  const { loading: currentUserLoading, userData: currentUser } = useUserData(
    user?.uid
  );
  const { loading, userData } = useUserData(id);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentHost = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (loading) return;
    if (currentUserLoading) return;

    if (userData) {
      if (!currentUser?.profileCompleted) {
        navigate("/onboarding");
      } else {
        setProfile(userData);
      }
    } else {
      return;
    }
  }, [user, userData, loading, currentUser, currentUserLoading, navigate]);

  const basicColumns = {
    username: "UserName",
    firstName: "First Name",
    lastName: "Last Name",
    gender: "Gender",
    maritalStatus: "Marital Status",
    dateOfBirth: "Date of Birth",
    timeOfBirth: "Time of Birth",
    placeOfBirth: "Place of Birth",
  };

  const horoscopeColumns = {
    subCaste: "Sub Caste",
    gothram: "Gothram",
    nakshatram: "Nakshatram",
    padam: "Padam",
    raasi: "Raasi",
  };

  const educationColumns = {
    education: "Education",
    employment: "Employment",
    salary: "Salary",
    salaryType: "Salary Type",
  };

  const familyColumns = {
    fathersOccupation: "Fathers Occupation",
    fathersName: "Fathers Name",
    mothersOccupation: "Mothers Occupation",
    mothersName: "Mothers Name",
    brothers: "Brothers",
    brothersMarital: "Brothers Marital",
    sisters: "Sisters",
    sistersMarital: "Sisters Marital",
    familyValues: "Family Values",
    familyType: "Family Type",
    familyStatus: "Family Status",
    nativePlace: "Native Place",
  };

  const titles = [
    {
      title: "Title",
      dataIndex: "title",
      render: (text) => <Text strong style={{ color: '#667eea' }}>{text}</Text>
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (text) => <Text style={{ color: '#2c3e50' }}>{text}</Text>
    },
  ];

  const basicData = Object.keys(basicColumns)
    .map((basic) => ({
      key: basic,
      title: basicColumns[basic],
      value:
        basic === "dateOfBirth"
          ? profile && profile[basic] && (
            profile[basic].toDate ?
              profile[basic].toDate().toDateString() :
              new Date(profile[basic]).toDateString()
          )
          : profile && profile[basic],
    }))
    .filter(({ key, title, value }) => key && title && value);

  const horoscopeData = Object.keys(horoscopeColumns)
    .map((horoscope) => ({
      key: horoscope,
      title: horoscopeColumns[horoscope],
      value: profile && profile[horoscope],
    }))
    .filter(({ key, title, value }) => key && title && value);

  const educationData = Object.keys(educationColumns)
    .map((education) => ({
      key: education,
      title: educationColumns[education],
      value: profile && profile[education],
    }))
    .filter(({ key, title, value }) => key && title && value);

  const familyData = Object.keys(familyColumns)
    .map((family) => ({
      key: family,
      title: familyColumns[family],
      value: profile && profile[family],
    }))
    .filter(({ key, title, value }) => key && title && value);

  const gunaPoints = getGunaPoints(
    currentUser?.gender === "Female"
      ? currentUser?.nakshatram +
        "|" +
        currentUser?.padam +
        "|" +
        profile?.nakshatram +
        "|" +
        profile?.padam
      : profile?.nakshatram +
        "|" +
        profile?.padam +
        "|" +
        currentUser?.nakshatram +
        "|" +
        currentUser?.padam
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      paddingTop: '150px',
      paddingBottom: '40px',
      paddingLeft: '20px',
      paddingRight: '20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Spin spinning={loading} size="large">
          {profile ? (
            <Card 
              style={{
                borderRadius: '20px',
                border: 'none',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
              }}
              bodyStyle={{ padding: '40px' }}
            >
              {/* Header with Back Button */}
              <div style={{ marginBottom: '30px' }}>
                <Button
                  onClick={() => navigate(-1)}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                  }}
                  icon={<ArrowLeftOutlined />}
                >
                  Go Back
                </Button>
              </div>

              <Row gutter={[40, 40]}>
                {/* Left Column - Images and Basic Info */}
                <Col xs={24} lg={10}>
                  <div style={{ textAlign: 'center' }}>
                    <Image.PreviewGroup
                      items={profile?.profileImages?.map((image) => image.url)}
                    >
                      <Image
                        width="100%"
                        height={400}
                        style={{ 
                          borderRadius: '16px',
                          objectFit: 'cover',
                          maxWidth: '400px'
                        }}
                        src={profile?.profileImages?.[0]?.url}
                      />
                    </Image.PreviewGroup>
                    
                    <div style={{ marginTop: '24px', marginBottom: '20px' }}>
                      <Title level={2} style={{ 
                        margin: '0 0 8px 0',
                        color: '#2c3e50'
                      }}>
                        {profile.firstName} {profile.lastName}
                      </Title>
                    </div>

                    {/* Guna Points Card */}
                    <Badge.Ribbon
                      text={
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <StarOutlined />
                          Guna Points: {gunaPoints}
                        </span>
                      }
                      color={gunaPoints >= 18 ? '#52c41a' : gunaPoints >= 12 ? '#faad14' : '#ff4d4f'}
                    >
                      <Card 
                        style={{
                          borderRadius: '16px',
                          border: 'none',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                          background: 'linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)'
                        }}
                        bodyStyle={{ padding: '20px' }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <Text style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            color: '#64748b'
                          }}>
                            My Nakshatram: <Text strong style={{ 
                              color: currentUser?.gender === "Male" ? "#667eea" : "#764ba2" 
                            }}>
                              {currentUser?.nakshatram}
                            </Text>
                          </Text>
                          {currentUser?.padam > 0 && (
                            <Text style={{ 
                              display: 'block', 
                              marginBottom: '8px',
                              color: '#64748b'
                            }}>
                              My Padam: <Text strong style={{ 
                                color: currentUser?.gender === "Male" ? "#667eea" : "#764ba2" 
                              }}>
                                {currentUser?.padam}
                              </Text>
                            </Text>
                          )}
                          <Text style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            color: '#64748b'
                          }}>
                            Their Nakshatram: <Text strong style={{ 
                              color: profile?.gender === "Male" ? "#667eea" : "#764ba2" 
                            }}>
                              {profile?.nakshatram}
                            </Text>
                          </Text>
                          {profile?.padam !== 0 && (
                            <Text style={{ 
                              display: 'block',
                              color: '#64748b'
                            }}>
                              Their Padam: <Text strong style={{ 
                                color: profile?.gender === "Male" ? "#667eea" : "#764ba2" 
                              }}>
                                {profile?.padam}
                              </Text>
                            </Text>
                          )}
                        </div>
                      </Card>
                    </Badge.Ribbon>
                  </div>
                </Col>

                {/* Right Column - Details */}
                <Col xs={24} lg={14}>
                  {/* Action Buttons */}
                  <Space style={{ marginBottom: '24px' }}>
                    {user && profile.uid && (
                      <LikeProfile
                        id={profile.uid}
                        userId={currentUser.uid}
                      />
                    )}
                    <Button
                      type="primary"
                      onClick={showModal}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: '#667eea',
                        borderRadius: '12px',
                        fontWeight: '600',
                        height: '48px',
                        padding: '0 24px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                      icon={<QrcodeOutlined />}
                    >
                      QR Code
                    </Button>
                  </Space>

                  {/* Information Cards */}
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Card 
                      title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>Basic Information</Title>}
                      style={{
                        borderRadius: '16px',
                        border: 'none',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <Table
                        size="middle"
                        columns={titles}
                        dataSource={basicData}
                        pagination={false}
                        showHeader={false}
                        style={{ 
                          '.ant-table-tbody > tr > td': {
                            borderBottom: '1px solid #f0f0f0',
                            padding: '12px 16px'
                          }
                        }}
                      />
                    </Card>

                    <Card 
                      title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>Education & Employment</Title>}
                      style={{
                        borderRadius: '16px',
                        border: 'none',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <Table
                        size="middle"
                        columns={titles}
                        dataSource={educationData}
                        pagination={false}
                        showHeader={false}
                      />
                    </Card>

                    {showMore && (
                      <>
                        <Card 
                          title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>Horoscope Information</Title>}
                          style={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Table
                            size="middle"
                            columns={titles}
                            dataSource={horoscopeData}
                            pagination={false}
                            showHeader={false}
                          />
                        </Card>

                        <Card 
                          title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>Family Information</Title>}
                          style={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Table
                            size="middle"
                            columns={titles}
                            dataSource={familyData}
                            pagination={false}
                            showHeader={false}
                          />
                        </Card>

                        <Card 
                          title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>About Self</Title>}
                          style={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Paragraph style={{ color: '#64748b', lineHeight: '1.6' }}>
                            {profile.aboutSelf}
                          </Paragraph>
                        </Card>

                        <Card 
                          title={<Title level={4} style={{ margin: 0, color: '#2c3e50' }}>Partner Requirements</Title>}
                          style={{
                            borderRadius: '16px',
                            border: 'none',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Paragraph style={{ color: '#64748b', lineHeight: '1.6' }}>
                            {profile.partnerPreferences}
                          </Paragraph>
                        </Card>
                      </>
                    )}

                    {/* Show More/Less Button */}
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Button
                        type="primary"
                        ghost
                        onClick={() => setShowMore(!showMore)}
                        style={{
                          borderColor: '#667eea',
                          color: '#667eea',
                          borderRadius: '12px',
                          fontWeight: '600',
                          height: '40px',
                          padding: '0 24px'
                        }}
                        icon={showMore ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      >
                        {showMore ? "Show Less" : "Show More"}
                      </Button>
                    </div>
                  </Space>
                </Col>
              </Row>
            </Card>
          ) : (
            <Card style={{
              borderRadius: '20px',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
              padding: '60px'
            }}>
              <Empty
                imageStyle={{ height: 120 }}
                description={<Text style={{ color: '#64748b', fontSize: '16px' }}>No profile found</Text>}
              />
            </Card>
          )}
        </Spin>

        {/* QR Code Modal */}
        <Modal
          centered
          width={350}
          title={
            <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
              Share Profile
            </Title>
          }
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={null}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <QRCode
              size={250}
              type="canvas"
              value={`${currentHost}/profile-page/${profile?.id}`}
              style={{ border: '8px solid #f0f0f0', borderRadius: '12px' }}
            />
            <Text style={{ display: 'block', marginTop: '16px', color: '#64748b' }}>
              Scan to view profile
            </Text>
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default Profile;